import { useRef, useEffect } from 'react';
import { ScrollArea } from '../ui/scroll-area';
import { Mic, Bot } from 'lucide-react';

/**
 * TranscriptPanel — live conversation display.
 * AI turns left-aligned, student turns right-aligned. While recording, a
 * live in-progress student bubble tracks `liveTranscript` in place at the
 * end of the list — no separate box, so growth just scrolls within this
 * panel instead of reflowing the rest of the page.
 */
export default function TranscriptPanel({ transcript, aiResponding, recording, liveTranscript }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript, aiResponding, recording, liveTranscript]);

  return (
    <div className="flex flex-col h-full min-h-0 w-full" data-testid="transcript-panel">
      <div className="px-4 py-3 border-b border-[#272B36] shrink-0">
        <h3 className="text-sm font-heading font-semibold text-white">Transcript</h3>
      </div>
      <ScrollArea className="flex-1 min-h-0 px-4 py-3">
        <div className="space-y-3">
          {transcript.map((entry, idx) => (
            <TranscriptBubble key={idx} entry={entry} />
          ))}
          {recording && (
            <div className="flex gap-2 items-start flex-row-reverse" data-testid="live-transcript-bubble">
              <div className="w-6 h-6 rounded-full bg-[#FF6B1A]/20 flex items-center justify-center shrink-0 mt-0.5">
                <Mic className="w-3 h-3 text-[#FF6B1A] animate-pulse" />
              </div>
              <div className="flex flex-col gap-0.5 max-w-[85%]">
                <span className="text-[10px] font-heading text-[#FF6B1A] px-1 self-end">Listening…</span>
                <div className="rounded-xl rounded-tr-sm px-3 py-2 text-[13px] leading-relaxed text-[#E4E4E7] bg-[#3D1F6E]/15 border border-dashed border-[#FF6B1A]/30">
                  {liveTranscript || ' '}
                </div>
              </div>
            </div>
          )}
          {aiResponding && (
            <div className="flex gap-2 items-start">
              <div className="w-6 h-6 rounded-full bg-[#3D1F6E]/40 flex items-center justify-center shrink-0 mt-0.5">
                <Bot className="w-3 h-3 text-[#A78BFA]" />
              </div>
              <div className="flex flex-col gap-0.5 max-w-[85%]">
                <span className="text-[10px] font-heading text-[#9BA1B0] px-1">Processing your answer…</span>
                <div className="bg-[#1C1F26] rounded-xl rounded-tl-sm px-3 py-2">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] animate-pulse" style={{ animationDelay: '0.2s' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] animate-pulse" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
    </div>
  );
}

function TranscriptBubble({ entry }) {
  const isAI = entry.role === 'ai';
  const isIntro = isAI && entry.is_intro;
  return (
    <div className={`flex gap-2 items-start ${isAI ? '' : 'flex-row-reverse'}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5
        ${isAI ? (isIntro ? 'bg-[#FFC233]/20' : 'bg-[#3D1F6E]/40') : 'bg-[#FF6B1A]/20'}`}>
        {isAI
          ? <Bot className={`w-3 h-3 ${isIntro ? 'text-[#FFC233]' : 'text-[#A78BFA]'}`} />
          : <Mic className="w-3 h-3 text-[#FF6B1A]" />
        }
      </div>
      <div className="flex flex-col gap-0.5 max-w-[85%]">
        {isIntro && (
          <span className="text-[10px] font-heading font-semibold text-[#FFC233] px-1">
            BotView
          </span>
        )}
        <div className={`rounded-xl px-3 py-2 text-[13px] leading-relaxed
          ${isAI
            ? `rounded-tl-sm text-[#E4E4E7] ${isIntro ? 'bg-[#FFC233]/10 border border-[#FFC233]/20' : 'bg-[#1C1F26]'}`
            : 'bg-[#3D1F6E]/30 rounded-tr-sm text-[#E4E4E7]'
          }`}
        >
          {entry.text}
        </div>
      </div>
    </div>
  );
}
